import React, {useEffect, useRef, useState} from "react";
import Sidebar from "../components/Sidebar";
import AddButton from "../components/AddButton";

import DatePicker, {registerLocale} from "react-datepicker";
import {it} from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";

import axios from "axios";
import moment from "moment";
import DateTimeSpan from "../components/DateTimeSpan";
import {toast} from "react-toastify";
import {privateApi} from "../api";
import RefreshButton from "../components/RefreshButton";
import DeleteButton from "../components/DeleteButton";

registerLocale("it", it);

// ---- TYPES ----
type Cliente = {
    id: number;
    name: string;
    surname: string;
    phone: string;
};

type Device = {
    id: number;
    model: string;
};

type CustomerResponse = {
    customers: Cliente[];
};

type DeviceResponse = {
    devices: Device[];
}

type AppointmentObject = {
    id: string;
    customer: string;
    device: string;
    description: string;
    date: string;
}

type AppointmentResponse = {
    appointments: AppointmentObject[];
}

function Appointment() {
    localStorage.setItem("focus", "appuntamenti")
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [appointments, setAppointments] = useState<AppointmentObject[]>([]);

    // ---- CLIENTE ----
    const [clienteInput, setClienteInput] = useState("");
    const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null);
    const [clienteResults, setClienteResults] = useState<Cliente[]>([]);
    const [showClienteDropdown, setShowClienteDropdown] = useState(false);

    // ---- DEVICE ----
    const [deviceInput, setDeviceInput] = useState("");
    const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
    const [deviceResults, setDeviceResults] = useState<Device[]>([]);
    const [showDeviceDropdown, setShowDeviceDropdown] = useState(false);

    // ---- DATE ----
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);

    // ----- Description ---
    const [description, setDescription] = useState("");

    const clienteRef = useRef<HTMLDivElement>(null);
    const deviceRef = useRef<HTMLDivElement>(null);

    const clienteCancelRef = useRef<AbortController | null>(null);
    const deviceCancelRef = useRef<AbortController | null>(null);

    // View
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [viewAppointmentId, setViewAppointmentId] = useState("");
    const [viewCustomer, setViewCustomer] = useState("");
    const [viewDevice, setViewDevice] = useState("");
    const [viewDate, setViewDate] = useState("");
    const [viewDescription, setViewDescription] = useState("");

    // ---- RESET ----
    const resetForm = () => {
        setClienteInput("");
        setSelectedCliente(null);
        setClienteResults([]);

        setDeviceInput("");
        setSelectedDevice(null);
        setDeviceResults([]);

        setSelectedDate(null);

        setDescription("");

        setShowClienteDropdown(false);
        setShowDeviceDropdown(false);
    };

    const loadAppointments = async () => {
        setLoading(true);
        try {
            const res = await privateApi.get<AppointmentResponse>("/cr-order-manager/v1/appointment");

            setAppointments(res.data.appointments ?? []);
        } catch (err) {
            if (axios.isAxiosError(err)) {
                const message =
                    err.response?.data?.message ||
                    "Errore lato server";
                toast.error(message);
            } else {
                toast.error("Errore non gestito");
            }
        } finally {
            setLoading(false);
        }
    };

    // ---- OUTSIDE CLICK ----
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (!clienteRef.current?.contains(e.target as Node)) {
                setShowClienteDropdown(false);
            }
            if (!deviceRef.current?.contains(e.target as Node)) {
                setShowDeviceDropdown(false);
            }
        };

        document.addEventListener("click", handler);
        return () => document.removeEventListener("click", handler);
    }, []);

    // ---- CLIENTE SEARCH ----
    useEffect(() => {
        if (!clienteInput) {
            setClienteResults([]);
            return;
        }

        const timeout = setTimeout(async () => {
            try {
                clienteCancelRef.current?.abort();

                const controller = new AbortController();
                clienteCancelRef.current = controller;

                const res = await privateApi.get<CustomerResponse>("/cr-order-manager/v1/customer/search",
                    {
                        params: {search: clienteInput},
                        signal: controller.signal,
                    }
                );

                setClienteResults(res.data.customers ?? []);
                setShowClienteDropdown(true);
            } catch {
                setClienteResults([]);
            }
        }, 500);

        return () => clearTimeout(timeout);
    }, [clienteInput]);

    // ---- DEVICE SEARCH ----
    useEffect(() => {
        if (!deviceInput) {
            setDeviceResults([]);
            return;
        }

        const timeout = setTimeout(async () => {
            try {
                deviceCancelRef.current?.abort();

                const controller = new AbortController();
                deviceCancelRef.current = controller;

                const res = await privateApi.get<DeviceResponse>("/cr-order-manager/v1/device/search", {
                    params: {search: deviceInput},
                    signal: controller.signal,
                });

                setDeviceResults(res.data.devices ?? []);
                setShowDeviceDropdown(true);
            } catch {
                setDeviceResults([]);
            }
        }, 500);

        return () => clearTimeout(timeout);
    }, [deviceInput]);

    useEffect(() => {
        loadAppointments();
    }, []);

    // ---- SUBMIT ----
    const handleSubmit = async () => {
        if (!selectedCliente) return toast.error("Seleziona un cliente valido");
        if (!selectedDevice) return toast.error("Seleziona un device valido");
        if (!selectedDate) return toast.error("Seleziona una data");
        if (!description) return toast.error("Inserisci una descrizione");

        const payload = {
            customerId: selectedCliente.id,
            deviceId: selectedDevice.id,
            startDate: moment(selectedDate).utc().format("DD-MM-YYYYTHH:mm"),
            endDate: moment(selectedDate).utc().format("DD-MM-YYYYTHH:mm"),
            description: description
        };

        try {
            await privateApi.post("/cr-order-manager/v1/appointment", payload);

            setIsModalOpen(false);
            resetForm();
            loadAppointments();
        } catch (err) {
            if (axios.isAxiosError(err)) {
                const message =
                    err.response?.data?.message ||
                    "Errore lato server";
                toast.error(message);
            } else {
                toast.error("Errore non gestito");
            }
        }
    };

    const deleteAppointment = async () => {
        try {
            await privateApi.delete(`/cr-order-manager/v1/appointment/${viewAppointmentId}`)
            setIsEditModalOpen(false);
            loadAppointments()
        } catch (err) {
            if (axios.isAxiosError(err)) {
                const message =
                    err.response?.data?.message ||
                    "Errore lato server";
                toast.error(message);
            } else {
                toast.error("Errore non gestito");
            }
        }
    }

    return (
        <>
            <Sidebar/>

            <main className="ml-64 p-6">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold">Appuntamenti</h1>
                    <div className="flex items-center gap-2">
                        <RefreshButton onClick={loadAppointments}/>
                        <AddButton
                            onClick={() => {
                                resetForm();
                                setIsModalOpen(true);
                            }}
                        />
                    </div>
                </div>

                <div className="space-y-6">
                    <div className="bg-white rounded-xl shadow p-5">

                        {/*Table*/}
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left">

                                <thead className="text-gray-500 border-b">
                                <tr>
                                    <th className="py-2">ID</th>
                                    <th className="py-2">Cliente</th>
                                    <th className="py-2">Device</th>
                                    <th className="py-2">Data</th>
                                </tr>
                                </thead>

                                <tbody className="divide-y">
                                {loading ? (
                                    <tr>
                                        <td
                                            colSpan={4}
                                            className="py-6 text-center text-gray-500"
                                        >
                                            Caricamento...
                                        </td>
                                    </tr>
                                ) : appointments.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={4}
                                            className="py-6 text-center text-gray-500"
                                        >
                                            Nessun appuntamento trovato
                                        </td>
                                    </tr>
                                ) : (appointments.map((appointment: AppointmentObject) => (
                                    <tr key={appointment.id} className="hover:bg-gray-50"
                                        onClick={() => {
                                            setViewAppointmentId(appointment.id);
                                            setViewCustomer(appointment.customer);
                                            setViewDevice(appointment.device);
                                            setViewDate(appointment.date);
                                            setViewDescription(appointment.description);
                                            setIsEditModalOpen(true);
                                        }}>
                                        <td className="py-2">{appointment.id}</td>
                                        <td className="py-2">{appointment.customer}</td>
                                        <td className="py-2">{appointment.device}</td>
                                        <td className="py-2"><DateTimeSpan date={appointment.date}/></td>
                                    </tr>
                                )))}

                                </tbody>

                            </table>
                        </div>

                    </div>
                </div>

                {isEditModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                        <div className="bg-white rounded-xl p-6 w-full max-w-md">
                            <h2 className="text-xl font-bold mb-4">
                                Vista Appuntamento
                            </h2>

                            <div className="space-y-3">
                                <input
                                    value={viewCustomer}
                                    disabled={true}
                                    className="w-full border p-2 rounded"
                                    placeholder="Cliente"
                                />
                                <input
                                    value={viewDevice}
                                    disabled={true}
                                    className="w-full border p-2 rounded"
                                    placeholder="Device"
                                />
                                <input
                                    value={moment.utc(viewDate, "DD-MM-YYYYTHH:mm")
                                        .local()
                                        .format("DD-MM-YYYY HH:mm")}
                                    disabled={true}
                                    className="w-full border p-2 rounded"
                                    placeholder="Data"
                                />
                                <textarea className="w-full border p-2 rounded"
                                          disabled={true}
                                          placeholder="Descrizione"
                                          value={viewDescription}
                                          rows={5}
                                          cols={40}/>
                            </div>

                            <div className="flex justify-end gap-2 mt-4">
                                <button
                                    onClick={() => {
                                        setIsEditModalOpen(false);
                                    }}
                                    className="px-4 py-2 bg-gray-200 rounded"
                                >
                                    Chiudi
                                </button>

                                <DeleteButton onClick={deleteAppointment}/>
                            </div>
                        </div>
                    </div>
                )}


                {isModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                        <div className="bg-white rounded-xl p-6 w-full max-w-md">
                            <h2 className="text-xl font-bold mb-4">
                                Nuovo Appuntamento
                            </h2>

                            <div className="space-y-3">
                                {/* CLIENTE */}
                                <div ref={clienteRef} className="relative">
                                    <input
                                        value={clienteInput}
                                        onChange={(e) => {
                                            setClienteInput(e.target.value);
                                            setSelectedCliente(null);
                                        }}
                                        className="w-full border p-2 rounded"
                                        placeholder="Cliente*"
                                    />

                                    {showClienteDropdown &&
                                        clienteResults.length > 0 && (
                                            <div
                                                className="absolute z-10 w-full bg-white border rounded mt-1 max-h-40 overflow-y-auto shadow">
                                                {clienteResults.map((c) => (
                                                    <div
                                                        key={c.id}
                                                        onClick={() => {
                                                            setClienteInput(
                                                                `${c.name} ${c.surname}`
                                                            );
                                                            setSelectedCliente(c);
                                                            setShowClienteDropdown(false);
                                                        }}
                                                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                                                    >
                                                        {c.name} {c.surname}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                </div>

                                {/* DEVICE */}
                                <div ref={deviceRef} className="relative">
                                    <input
                                        value={deviceInput}
                                        onChange={(e) => {
                                            setDeviceInput(e.target.value);
                                            setSelectedDevice(null);
                                        }}
                                        className="w-full border p-2 rounded"
                                        placeholder="Device*"
                                    />

                                    {showDeviceDropdown &&
                                        deviceResults.length > 0 && (
                                            <div
                                                className="absolute z-10 w-full bg-white border rounded mt-1 max-h-40 overflow-y-auto shadow">
                                                {deviceResults.map((d) => (
                                                    <div
                                                        key={d.id}
                                                        onClick={() => {
                                                            setDeviceInput(d.model);
                                                            setSelectedDevice(d);
                                                            setShowDeviceDropdown(false);
                                                        }}
                                                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                                                    >
                                                        {d.model}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                </div>

                                {/* DATE */}
                                <DatePicker
                                    selected={selectedDate}
                                    onChange={(date: Date | null) =>
                                        setSelectedDate(date)
                                    }
                                    showTimeSelect
                                    dateFormat="dd/MM/yyyy HH:mm"
                                    locale="it"
                                    placeholderText="Data e ora*"
                                    className="w-full border p-2 rounded"
                                />

                                <textarea className="w-full border p-2 rounded"
                                          placeholder="Descrizione*"
                                          value={description}
                                          rows={5}
                                          cols={40}
                                          onChange={(e) => {
                                              setDescription(e.target.value);
                                          }}/>
                            </div>

                            <div className="flex justify-end gap-2 mt-4">
                                <button
                                    onClick={() => {
                                        setIsModalOpen(false);
                                        resetForm();
                                    }}
                                    className="px-4 py-2 bg-gray-200 rounded"
                                >
                                    Annulla
                                </button>

                                <button
                                    onClick={handleSubmit}
                                    className="px-4 py-2 bg-blue-900 text-white rounded"
                                >
                                    Salva
                                </button>
                            </div>

                            <span>(*) Campo obbligatorio</span>
                        </div>
                    </div>
                )}
            </main>
        </>
    )
        ;
}

export default Appointment;